import Penpal from '../utils/penpal';
import SubscriptionManager from './manager';
import { log } from '../utils/debug';
import { popup } from '../utils/http';
import Event from '../const/event';
import SubscribingInProgressError from '../error/SubscribingInProgressError';
import CleverPushError from '../error/CleverPushError';
import {
  browser,
  browserVersion
} from '../utils/env';

export default class HttpSubscriptionManager extends SubscriptionManager {
  constructor(config, api, triggerEvent) {
    super(config, api, triggerEvent);

    this.isSubscribing = false;
    this.iframeMessenger = null;
    this.popupMessenger = null;

    this.setTopics = this.setTopics.bind(this);

    if (this.config.subdomain && this.config.subdomain.length && this.config.loadIframe !== false) {
      const origin = `https://${this.config.subdomain.toLowerCase()}.${this.config.cleverpushDomain || 'cleverpush.com'}`;

      const iframeContainer = document.createElement('div');
      iframeContainer.style.display = 'none';
      document.body.appendChild(iframeContainer);

      Penpal.debug = log.getLevel() < 2;
      const connection = Penpal.connectToChild({
        url: `${origin}/iframe?origin=${encodeURIComponent(location.origin)}`,
        appendTo: iframeContainer,
        methods: {
          onSubscribed: (subscriptionId) => {
            this.triggerEvent(Event.SUBSCRIBED, subscriptionId);
          }
        }
      });

      connection.iframe.style.display = 'none';

      connection.promise.then((child) => {
        log.debug('child ready', child);
        this.iframeMessenger = child;

        child.setConfig(this.config).then(() => {
          /*
           child.getStorage().then((storageItems) => {
           const storageKeys = Object.keys(storageItems);
           localStorage.
           });
           */
          this.triggerEvent(Event.INITIALIZED);
        });

        this.getTopics().then(topics => this.topics = topics);
      }).catch(() => {
        log.debug('iframe connection failed');
        this.triggerEvent(Event.INITIALIZED);
      });
    } else {
      log.debug('iframe subdomain not available');
      this.triggerEvent(Event.INITIALIZED);
    }

    // unsubscribe via iframe if user sets host permission to denied + subscribe if permission is granted
    if (navigator.permissions && !(browser.name === 'Firefox' && browserVersion < 46)) {
      navigator.permissions.query({ name: 'notifications' }).then((permissionStatus) => {
        permissionStatus.onchange = () => {
          if (!this.isSubscribing) {
            if (window.Notification.permission === 'denied') {
              this.unsubscribe();
            } else if (window.Notification.permission === 'granted') {
              this.isSubscribed().then((enabled) => {
                if (!enabled) {
                  this.triggerEvent(Event.PERMISSION_RE_GRANTED);
                }
              });
            }
          }
        };
      });
    }
  }

  isSubscribed() {
    return new Promise((resolve) => {
      const syncAndResolve = () => {
        this.storageManager.checkIfShouldSync().then((shouldSync) => {
          if (shouldSync) {
            this.sync().then(() => {
              log.debug('hostManager subscribed = true (1)');
              this.subscribed = true;
              resolve(true);
            }).catch((err) => {
              log.warn('error while syncing', err);
              log.debug('hostManager subscribed = true (2)');
              this.subscribed = true;
              resolve(true);
            });
          }
        });
      };

      if (this.iframeMessenger) {
        this.iframeMessenger.isSubscribed().then((isSubscribed) => {
          log.debug('isSubscribed from iframe', isSubscribed);
          if (isSubscribed) {
            log.debug('hostManager subscribed = true (3)');
            this.subscribed = true;
            resolve(true);
          } else {
            // TODO: if push-subscription-id on host is given, then old script -> dont prompt again
            // else: normal prompting
            this.storageManager.isSubscribed(false).then((storageStatus) => {
              if (storageStatus) {
                syncAndResolve();
              } else {
                log.debug('hostManager subscribed = false (4)');
                this.subscribed = false;
                resolve(false);
              }
            });
          }
        }).catch((err) => {
          if (err) {
            log.debug('isSubscribed err', err);
            log.error(err);
          }
          log.debug('hostManager subscribed = false (5)');
          this.subscribed = false;
          resolve(false);
        });
      } else {
        log.debug('hostManager subscribed = false (6)');
        this.subscribed = false;
        resolve(false);
      }
    });
  }

  canSubscribe() {
    return new Promise((resolve, reject) => {
      if (this.iframeMessenger) {
        this.iframeMessenger.canSubscribe().then(({ result, message, reason }) => {
          log.debug('canSubscribe from iframe', result);
          if (result === true) {
            this.storageManager.canSubscribe().then((storageStatus) => {
              log.debug('storageStatus canSubscribe', storageStatus);
              if (storageStatus) {
                this.isSubscribed().then((isSubscribed) => {
                  if (isSubscribed) {
                    reject(new CleverPushError('User is already subscribed', 'subscribed'));
                  } else {
                    resolve(true);
                  }
                });
              } else {
                reject(new CleverPushError('User has manually unsubscribed or denied notifications', 'unsubscribed'));
              }
            });
          } else {
            reject(new CleverPushError(message, reason));
          }
        }, (err) => {
          log.debug('canSubscribe err iframe', err);
          reject(err);
        });
      } else {
        this.storageManager.canSubscribe().then((storageStatus) => {
          log.debug('storageStatus canSubscribe', storageStatus);
          if (storageStatus) {
            this.isSubscribed().then((isSubscribed) => {
              if (isSubscribed) {
                reject(new CleverPushError('User is already subscribed', 'subscribed'));
              } else {
                resolve(true);
              }
            });
          } else {
            reject(new CleverPushError('User has manually unsubscribed or denied notifications', 'unsubscribed'));
          }
        });
      }
    });
  }

  subscribeForce() {
    return this.subscribe();
  }

  subscribe() {
    let popupWindow;
    let customDomain = this.config.popupCustomDomainEnabled && this.config.popupDomain;
    let popupUrl;

    if (!this.isSubscribing) {
      let popupDomain = `${(this.config.subdomain || this.config.channelSubdomain)}.${this.config.cleverpushDomain || 'cleverpush.com'}`;
      let popupPath = '/subscribe';

      if (customDomain) {
        popupDomain = this.config.popupDomain;
        popupPath = this.config.popupSubscribePath || '/';
      }

      popupUrl = `https://${popupDomain}${popupPath}?origin=${location.origin}`;

      if (this.config.confirmAlertTestId) {
        popupUrl += `&confirmAlertTestId=${this.config.confirmAlertTestId}`;
      }

      popupWindow = popup(popupUrl, this.config.popupWidth || 800, this.config.popupHeight || 600, this.config.popupTop || undefined, this.config.popupLeft || undefined);
    }

    if (popupWindow && customDomain) {
      Penpal.debug = log.getLevel() < 2;
      const connection = Penpal.connectToChild({
        childWindow: popupWindow,
        url: popupUrl,
        timeout: 30 * 1000,
        methods: {
          setSubscribed: (subscriptionId) => {
            if (subscriptionId) {
              this.storageManager.setSubscribed(subscriptionId);
              if (this.iframeMessenger) {
                this.iframeMessenger.setSubscribed(subscriptionId);
              }

              this.triggerEvent(Event.SUBSCRIBED, subscriptionId);
            }

            return Promise.resolve();
          }
        }
      });

      connection.promise.then((child) => {
        log.debug('popup connection ready', child);

        child.setConfig(this.config).then(() => {
          
        });
      }).catch((err) => {
        log.error('popup connection error', err);
      });
    }

    return new Promise((resolve, reject) => {
      if (this.isSubscribing) {
        log.error('A subscription process is already in progress.');
        reject(new SubscribingInProgressError());
      } else {
        this.isSubscribing = true;

        // this.storageManager.setAllowed();


        /*
        if (fromClickEvent && !isAndroid()) {
          popupWindow = popup('', 10, 10, true);
          if (popupWindow) {
            popupWindow.document.write('<html><head><link rel="stylesheet" href="//maxcdn.bootstrapcdn.com/font-awesome/4.5.0/css/font-awesome.min.css"><style>body { margin: 0; padding: 0; width: 100%; height: 100%; color: #555; font-family: sans-serif; } body, html { height: 100%; overflow: hidden; } .loading { margin-top: 10px; text-align: center; font-size: 20px; padding: 0;}</style></head><body><div class="loading"><i class="fa fa-spinner fa-spin fa-2x"></i></div></body></html>');
          }
        }
        */

        const checkIfPopupClosed = (checkPopupWindow, callback) => {
          if (checkPopupWindow) {
            let checkInterval;

            const onClosed = () => {
              if (checkInterval) {
                clearInterval(checkInterval);
                checkInterval = null;
              }
              callback();
            };

            const check = () => {
              try {
                if (!checkPopupWindow || checkPopupWindow.closed) {
                  onClosed();
                }
              } catch (err) {
                onClosed();
              }
            };

            // TODO: unload event fires too early, check why
            // checkPopupWindow.addEventListener('unload', onClosed.bind(this));
            checkInterval = setInterval(check, 100);
          }
        };

        const successCallback = () => {
          this.isSubscribing = false;
          // TODO: We can't get the perm. via iFrame anymore.
          /*
          this.getNotificationPermission().then((permission) => {
            if (permission === 'default') {
            } else {
            }
          });
          */

          this.getSubscriptionId().then((subscriptionId) => {
            if (subscriptionId) {
              this.storageManager.setSubscribed(subscriptionId);
              if (this.iframeMessenger) {
                this.iframeMessenger.setSubscribed(subscriptionId);
              }

              this.triggerEvent(Event.SUBSCRIBED, subscriptionId);

              if (this.config.confirmAlertSelectTopicsLater && !this.config.confirmAlertHideChannelTopics) {
                this.confirm.appendConfirmBox(this.config.confirmAlertSelectTopicsLaterTheme || 'cleverpush-confirm-default', true);
              }
              if (this.config.confirmAlertSelectAttributesLater) {
                import('../selectAttributes').then(({ default: SelectAttributes }) => {
                  this.selectAttributes = new SelectAttributes(this.config, this);
                  this.selectAttributes.appendConfirmBox(this.config.confirmAlertSelectTopicsLaterTheme || 'cleverpush-confirm-default', true);
                });
              }
              resolve('granted');
            } else {
              this.storageManager.setClosed();
              resolve('default');
            }
          });
        };

        if (!popupWindow) {
          // location.href = popupUrl;
          log.error('Popup blocked - Somehow we tried to open a popup without a click event.');
          successCallback();
        } else {
          checkIfPopupClosed(popupWindow, successCallback);
        }

        /*
        if (fromClickEvent) {

        } else {
          this.iframeMessenger.subscribe().then(({ permission, serviceWorkerInstalled }) => {
            const successCallback = () => {
              this.isSubscribing = false;
              this.getSubscriptionId().then((subscriptionId) => {
                this.storageManager.setSubscribed(subscriptionId);
                this.triggerEvent(Event.SUBSCRIBED, subscriptionId);
              });
              resolve(permission);
            };

            // hide backdrop
            if (this.confirm) {
              this.confirm.hideBackdrop();
            }

            if (permission === 'granted') {
              if (popupWindow) {
                popupWindow.document.location.href = popupUrl;
                checkIfPopupClosed(popupWindow, successCallback);
              } else if (!serviceWorkerInstalled) {
                const clickListener = () => {
                  // make sure we fire only once
                  document.removeEventListener('click', clickListener);


                  popupWindow = popup(popupUrl, 10, 10, true);
                  if (popupWindow) {
                    setTimeout(() => { window.focus(); }, 500);
                    checkIfPopupClosed(popupWindow, successCallback);
                  } else {
                    location.href = popupUrl;
                  }
                  console.log(popupWindow);
                };

                // attach 'click' handler to document to open popup
                document.addEventListener('click', clickListener);

              } else {
                successCallback();
              }
            } else {
              if (popupWindow) {
                popupWindow.close();
              }

              log.error(permission);
              this.isSubscribing = false;
              resolve(permission);
            }
          });
        }
        */
      }
    });
  }

  unsubscribe(notManually) {
    if (this.iframeMessenger) {
      return this.iframeMessenger.unsubscribe(notManually).then(() => {
        this.storageManager.setUnsubscribed(notManually);
        this.triggerEvent(Event.UNSUBSCRIBED);
      });
    }

    this.storageManager.setUnsubscribed(notManually);
    this.triggerEvent(Event.UNSUBSCRIBED);
  }

  getSubscriptionId() {
    return new Promise((resolve) => {
      this.iframeMessenger.getSubscriptionId().then((subscriptionId) => {
        resolve(subscriptionId);
      });
    });
  }

  hasNotificationPermission() {
    return new Promise((resolve) => {
      if (this.config.env === 'PREVIEW') {
        return resolve(false);
      }

      if (this.iframeMessenger) {
        this.iframeMessenger.hasNotificationPermission().then((hasNotificationPermission) => {
          resolve(hasNotificationPermission);
        });
      } else {
        resolve(false);
      }
    });
  }

  getNotificationPermission() {
    return new Promise((resolve) => {
      if (this.iframeMessenger) {
        this.iframeMessenger.getNotificationPermission().then((permission) => {
          resolve(permission);
        });
      } else {
        resolve();
      }
    });
  }

  setTopics(topics) {
    this.topics = topics;
    if (this.iframeMessenger) {
      return this.iframeMessenger.setTopics(topics);
    }
    return Promise.resolve();
  }

  getTopics() {
    if (this.iframeMessenger) {
      return this.iframeMessenger.getTopics();
    }
    return Promise.resolve([]);
  }

  getStoredNotifications() {
    return new Promise((resolve) => {
      if (this.iframeMessenger) {
        this.iframeMessenger.getStoredNotifications().then((notifications) => {
          resolve(notifications);
        });
      } else {
        resolve([]);
      }
    });
  }

  deleteStoredNotification(id) {
    return new Promise((resolve) => {
      if (this.iframeMessenger) {
        this.iframeMessenger.deleteStoredNotification(id).then(resolve);
      } else {
        resolve();
      }
    });
  }
}
